import axios, { CancelTokenSource } from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';

import { getSystemEndpoints, getSystemEndpointsByNextUrl } from './api';
import { DiallerType, SystemEndpointsResponse } from './domain';

export { DiallerType } from './domain';

type Options = {
  shouldFetch?: boolean;
  diallerType?: DiallerType;
};

const useSystemEndpointList = (options: Options = { shouldFetch: true, diallerType: DiallerType.Connect }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const [list, setList] = useState<string[]>([]);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [nextUrl, setNextUrl] = useState<string | null>(null);
  const axiosCancelRef = useRef<CancelTokenSource>(axios.CancelToken.source());
  const observer = useRef<IntersectionObserver | undefined>(undefined);
  const shouldFetch = options.shouldFetch ?? true;
  const diallerType = options.diallerType ?? DiallerType.Connect;

  const getNextPage = useCallback(async () => {
    if (nextUrl !== null) {
      setLoading(true);
      setError(false);

      let resp: SystemEndpointsResponse | undefined;
      try {
        axiosCancelRef.current = axios.CancelToken.source();
        resp = await getSystemEndpointsByNextUrl(nextUrl, axiosCancelRef.current);
      } catch (e) {
        setError(true);
        setLoading(false);
        return;
      }

      // Returns undefined if request is canceled
      if (resp === undefined) return;

      setList((prev) => [...prev, ...resp!.endpoints]);
      setHasMore(resp.nextPageUrl !== null);
      setNextUrl(resp.nextPageUrl);
      setLoading(false);
    }
  }, [nextUrl]);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(false);

    let resp: SystemEndpointsResponse | undefined;
    try {
      axiosCancelRef.current = axios.CancelToken.source();
      resp = await getSystemEndpoints(diallerType, axiosCancelRef.current);
    } catch (e) {
      setError(true);
      setLoading(false);
      return;
    }

    // Returns undefined if request is canceled
    if (resp === undefined) return;

    setList(resp.endpoints);
    setHasMore(resp.nextPageUrl !== null);
    setNextUrl(resp.nextPageUrl);
    setLoading(false);
  }, [diallerType]);

  /** Ref watches for element view intersection and loads more results. Note: Should only be assigned to last element in
   * a list
   * */
  const intersectionObserverRef = useCallback(
    (node: any) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          getNextPage();
        }
      });
      if (node) observer.current.observe(node);
    },
    [loading, hasMore, getNextPage],
  );

  useEffect(() => {
    setList([]);
  }, [shouldFetch]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(false);

      let resp: SystemEndpointsResponse | undefined;
      try {
        axiosCancelRef.current = axios.CancelToken.source();
        resp = await getSystemEndpoints(diallerType, axiosCancelRef.current);
      } catch (e) {
        setError(true);
        setLoading(false);
        return;
      }

      // Returns undefined if request is canceled
      if (resp === undefined) return;

      setList((prev) => [...prev, ...resp!.endpoints]);
      setHasMore(resp.nextPageUrl !== null);
      setNextUrl(resp.nextPageUrl);
      setLoading(false);
    };

    if (shouldFetch) {
      load();
    }

    return () => {
      // Cancel request if it has already been executed
      axiosCancelRef.current.cancel();
    };
  }, [shouldFetch, diallerType]);

  return { loading, error, list, intersectionObserverRef, reload };
};

export default useSystemEndpointList;
